/**
 * DocumentsTab.tsx
 *
 * Patient Documents tab — legacy functional parity (patient_details pageID 24).
 *
 * The legacy tab is a view of the patient's document activity log
 * (comm_log type 4) rendered as a Date / Subject / File / User table with a
 * client-side search box. Clicking a row opens/downloads the underlying file
 * from the docstore.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, FileText, Plus, X } from 'lucide-react';
import {
  listPatientDocuments,
  openDocument,
  downloadDocument,
  getDocumentLibrary,
  addPatientDocument,
  type PatientDocument,
  type DocumentLibraryItem,
} from '@/services/patient-documents.service';
import { getPlansByPatient } from '@/services/plans.service';
import { getErrorMessage } from '@/services/apiClient';

interface DocumentsTabProps {
  patientId: number;
}

// ─── New Document modal ─────────────────────────────────────────────────────

interface NewDocumentModalProps {
  patientId: number;
  onClose: () => void;
  onAdded: () => void;
}

function NewDocumentModal({ patientId, onClose, onAdded }: NewDocumentModalProps) {
  const [categoryId, setCategoryId] = useState<number>(0);
  const [pickerSearch, setPickerSearch] = useState('');
  const [planId, setPlanId] = useState<number>(0);

  const { data: library, isLoading, isError, error } = useQuery({
    queryKey: ['document-library', patientId, categoryId],
    queryFn: () => getDocumentLibrary(patientId, categoryId || undefined),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['patient-plans-for-documents', patientId],
    queryFn: () => getPlansByPatient(patientId),
  });

  const mutation = useMutation({
    mutationFn: (doc: DocumentLibraryItem) => addPatientDocument(patientId, doc.documentId, planId),
    onSuccess: (created) => {
      // Legacy parity: adding a Dymo `.label` immediately downloads the
      // generated label (legacy openLabel does window.location to it).
      if (created?.fileName?.toLowerCase().endsWith('.label')) {
        void downloadDocument(patientId, created.fileName);
      }
      onAdded();
    },
  });

  const documents = useMemo(() => {
    const all = library?.documents ?? [];
    const term = pickerSearch.trim().toLowerCase();
    if (!term) return all;
    return all.filter((d) =>
      [d.title, d.description, d.categoryName, d.fileName]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [library, pickerSearch]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && !mutation.isPending) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Add Document</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value={0}>All categories</option>
            {(library?.categories ?? []).map((c) => (
              <option key={c.categoryId} value={c.categoryId}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="search library"
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Plan selection — keyword substitution uses the chosen plan's data */}
        <div className="px-5 py-3 border-b border-slate-100">
          <p className="text-xs font-medium text-slate-600 mb-2">Plan</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 max-h-28 overflow-auto">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="documentPlan"
                checked={planId === 0}
                onChange={() => setPlanId(0)}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              None
            </label>
            {plans.map((plan) => (
              <label
                key={plan.planId}
                className="inline-flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="radio"
                  name="documentPlan"
                  checked={planId === plan.planId}
                  onChange={() => setPlanId(plan.planId)}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                {plan.name?.trim() || `Plan ${plan.planId}`}
                {plan.stageName ? (
                  <span className="text-xs text-slate-400">({plan.stageName})</span>
                ) : null}
              </label>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-auto flex-1 p-5 space-y-2">
          {isLoading && (
            <div className="py-10 text-center text-sm text-slate-500">Loading library…</div>
          )}
          {isError && !isLoading && (
            <div className="py-10 text-center text-sm text-red-600">
              {getErrorMessage(error)}
            </div>
          )}
          {!isLoading && !isError && documents.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-500">
              No documents available.
            </div>
          )}
          {!isLoading &&
            !isError &&
            documents.map((doc) => (
              <button
                key={doc.documentId}
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(doc)}
                className="w-full flex items-center gap-3 text-left border border-slate-200 rounded-lg px-4 py-3 hover:border-indigo-400 hover:bg-indigo-50 transition-colors disabled:opacity-50"
              >
                <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">
                    {doc.title || doc.fileName}
                  </span>
                  {doc.categoryName && (
                    <span className="block text-xs text-slate-400">{doc.categoryName}</span>
                  )}
                </span>
                <Plus className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            ))}
        </div>

        {mutation.isError && (
          <div className="px-5 py-3 border-t border-slate-200 text-sm text-red-600">
            {getErrorMessage(mutation.error)}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  // Accept 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS'
  const datePart = value.split(' ')[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function DocumentsTab({ patientId }: DocumentsTabProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [showNewDocument, setShowNewDocument] = useState(false);

  const {
    data: documents = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['patient-documents', patientId],
    queryFn: () => listPatientDocuments(patientId),
  });

  function handleAdded() {
    queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
    setShowNewDocument(false);
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return documents;
    return documents.filter((doc) => {
      const haystack = [
        formatDate(doc.date),
        doc.subject,
        doc.fileName,
        doc.userName,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [documents, search]);

  async function handleOpen(doc: PatientDocument) {
    if (!doc.fileName) return;
    try {
      setOpeningId(doc.commLogId);
      await openDocument(patientId, doc.fileName);
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header row */}
      {/*
        SCRUM-1429. The search wrapper is a fixed 224px (`w-56`) and the action button
        is `whitespace-nowrap`, so at 375px the pair plus the title plus `px-6` cannot
        share a line and the button was pushed past the right edge. Below `md` the row
        wraps, the search takes the full width, and the button follows on its own line.
      */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 pt-4 pb-2 max-md:flex-wrap max-md:gap-2 max-md:px-4">
        <h3 className="text-sm font-semibold text-slate-700">Documents</h3>

        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <div className="relative w-56 max-w-full max-md:w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="search documents"
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowNewDocument(true)}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap max-md:w-full max-md:justify-center"
          >
            <Plus className="w-4 h-4" />
            New Document
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 text-sm text-slate-500">
          Loading documents…
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="px-6 py-12 text-sm text-red-600 text-center">
          Failed to load documents: {getErrorMessage(error)}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && documents.length === 0 && (
        <div className="mx-6 flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-xl bg-slate-50">
          <FileText className="w-8 h-8 text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm font-medium">No documents yet.</p>
        </div>
      )}

      {/* No search results */}
      {!isLoading && !isError && documents.length > 0 && filtered.length === 0 && (
        <div className="px-6 py-12 text-sm text-slate-500 text-center">
          No documents match “{search}”.
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && filtered.length > 0 && (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-32">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-44">
                  User
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filtered.map((doc) => {
                const clickable = Boolean(doc.fileName);
                return (
                  <tr
                    key={doc.commLogId}
                    onClick={clickable ? () => handleOpen(doc) : undefined}
                    className={`transition-colors ${
                      clickable ? 'hover:bg-indigo-50 cursor-pointer' : ''
                    } ${openingId === doc.commLogId ? 'opacity-60' : ''}`}
                  >
                    <td className="px-6 py-3 text-slate-600 whitespace-nowrap">
                      {formatDate(doc.date)}
                    </td>
                    <td className="px-6 py-3 font-medium text-slate-800">
                      {doc.subject || '—'}
                    </td>
                    <td className="px-6 py-3 text-slate-600">
                      {doc.fileName ? (
                        <span className="inline-flex items-center gap-1.5 text-indigo-600">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate max-w-xs">{doc.fileName}</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600 whitespace-nowrap">
                      {doc.userName || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New Document modal */}
      {showNewDocument && (
        <NewDocumentModal
          patientId={patientId}
          onClose={() => setShowNewDocument(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
}
